import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { clearSession, storage } from "@/api/client";
import * as endpoints from "@/api/endpoints";

type AuthState = {
  email: string | null;
  isSignedIn: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithCode: (code: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState(() => localStorage.getItem(storage.email));

  const signIn = useCallback(async (nextEmail: string, password: string) => {
    const session = await endpoints.login(nextEmail, password);
    localStorage.setItem(storage.accessToken, session.accessToken);
    localStorage.setItem(storage.email, session.email);
    setEmail(session.email);
  }, []);

  /**
   * The other way in: a code mailed to the operator.
   *
   * Same identity and same token as a password sign-in — the difference is
   * only which credential proved it. Kept because the password lives in an
   * environment variable on a server and is typed once a quarter, so losing it
   * is the normal case rather than the exception.
   */
  const signInWithCode = useCallback(async (code: string) => {
    const session = await endpoints.verifyLoginCode(code);
    localStorage.setItem(storage.accessToken, session.accessToken);
    localStorage.setItem(storage.email, session.email);
    setEmail(session.email);
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    setEmail(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      email,
      signInWithCode,
      isSignedIn: Boolean(email && localStorage.getItem(storage.accessToken)),
      signIn,
      signOut,
    }),
    [email, signIn, signInWithCode, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
